// ─── Dashboard Canvas Route ───
// /app/dashboards/$dashboardId — the canvas page (docs/design/components/
// dashboard-engine.md, this spec's main subject). `edit=true` (set only by
// the §1.6 page-empty-state "Create dashboard" CTA, and Phase 12 Batch 3
// slice 3c's "Make a copy" flow) starts the grid directly in edit mode on a
// freshly-created blank canvas.
//
// `ws`/`space`/`company`/`from`/`to` (Phase 12 Batch 3 slice 3c) — the
// `DashboardToolbar`'s scope+date overlay, folding the retiring `/app/reports`
// toolbar into the canvas (see `features/dashboards/useDashboardViewParams.ts`).
// Kept as plain optional strings (not `uuid()`/`date()`-refined like
// `routes/app/reports.tsx`'s stricter schema) per this slice's contract —
// `useDashboardViewParams` reads them as opaque strings and never persists an
// invalid one back to the URL.

import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { parseEntityRef, dashboardPath } from '@a8-planner/shared/utils/entity-url';
import { Breadcrumb, type BreadcrumbSegment } from '../../features/layout/Breadcrumb';
import { DashboardCanvasPage } from '../../features/dashboards/DashboardCanvasPage';
import { useDashboard } from '../../features/dashboards/useDashboards';
import { dashboardsApi } from '../../lib/api/dashboards';
import { ApiError } from '../../lib/api-client';

const dashboardCanvasSearchSchema = z.object({
  edit: z.boolean().optional().catch(undefined),
  /** Workspace narrowing scope. Absent = all accessible workspaces. */
  ws: z.string().optional().catch(undefined),
  /** Space narrowing scope. Absent = all accessible spaces in scope. */
  space: z.string().optional().catch(undefined),
  /** ADR 0016 + scope-tree-picker.md resolution #1 — company scope narrowing.
   * Absent = the viewer's active company; `'all'` = merged across every
   * accessible company; any other string = a specific company browsed
   * locally via the scope tree. */
  company: z.string().optional().catch(undefined),
  /** View-window start (ISO date). Absent = server default. */
  from: z.string().optional().catch(undefined),
  /** View-window end (ISO date). Absent = server default. */
  to: z.string().optional().catch(undefined),
});

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ─── Friendly URLs (GHE #315) ───
// The `$dashboardId` param accepts BOTH shapes, which is what makes this migration free of a
// redirect table:
//   • `project-status-000001` — the canonical friendly ref. Resolved server-side by its trailing
//     6-hex short id; a stale slug (someone renamed the dashboard) still resolves and then
//     self-heals via a canonical redirect, exactly like `/app/l/$listRef` (ADR 0012 D3).
//   • a raw UUID — every link bookmarked before this shipped. Resolved by `getById` and then
//     UPGRADED to the friendly path, so an old bookmark quietly stops showing an internal id.
// The param keeps its `dashboardId` name to avoid churning the route tree and five call sites;
// downstream consumers always receive the real UUID from loader data, never the ref.
async function resolveDashboardParam(param: string): Promise<string> {
  if (UUID_RE.test(param)) {
    // Legacy UUID link: confirm it is readable, then upgrade the URL to the friendly form.
    try {
      const { data } = await dashboardsApi.getById(param);
      throw redirect({
        to: dashboardPath(data.name, data.id) as '/app',
        search: (prev) => prev,
        replace: true,
      });
    } catch (err) {
      // 404 covers nonexistent AND not-readable — indistinguishable by design, so both become
      // a route not-found rather than leaking which one it was.
      if (err instanceof ApiError && err.status === 404) throw notFound();
      throw err;
    }
  }

  const parsed = parseEntityRef(param);
  if (!parsed) throw notFound();

  try {
    const { data } = await dashboardsApi.resolve(parsed.short, parsed.slug);
    if (parsed.slug !== data.canonicalSlug) {
      throw redirect({ to: data.canonicalPath as '/app', search: (prev) => prev, replace: true });
    }
    return data.id;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) throw notFound();
    throw err;
  }
}

export const Route = createFileRoute('/app/dashboards/$dashboardId')({
  validateSearch: dashboardCanvasSearchSchema,
  loader: ({ params }): Promise<string> => resolveDashboardParam(params.dashboardId),
  component: RouteComponent,
});

function RouteComponent(): React.ReactElement {
  // The RESOLVED uuid — never the URL ref. Everything downstream (`useDashboard`, the canvas,
  // widget-data) keys on the real id exactly as before this route learned friendly refs.
  const dashboardId = Route.useLoaderData();
  const { edit } = Route.useSearch();
  const { t } = useTranslation('common');
  // GHE #283 item 2 — Breadcrumb was wired into exactly one route
  // (`/app/l/$listRef`); this one had no path at all. `useDashboard` is the
  // SAME already-resolved query `DashboardCanvasPage` itself uses (shared
  // TanStack Query cache entry, `dashboardKeys.byId` — no second network
  // request), just consumed one level up so the breadcrumb's dashboard-name
  // segment doesn't wait on that component to mount.
  const { dashboard } = useDashboard(dashboardId);
  const breadcrumbSegments: BreadcrumbSegment[] = [
    { label: t('nav.dashboards'), href: '/app/dashboards' },
    { label: dashboard?.name ?? dashboardId },
  ];
  return (
    <>
      <Breadcrumb segments={breadcrumbSegments} />
      <DashboardCanvasPage dashboardId={dashboardId} initialEditMode={edit} />
    </>
  );
}
